import { computed } from 'vue'
import { useUserStore } from '@/stores/useUser'
import { useSessionStore } from '@/entities/Session/model'
import { authorize, logout } from '@/shared/lib/auth/authorize'
import { checkAuthState } from '@/shared/lib/auth/checkAuth'
import { 
    ensureValidToken, 
    refreshToken, 
    forceRefreshToken,
    isTokenExpired, 
    isTokenExpiringSoon 
} from '@/shared/lib/auth/tokenManager'
import { 
    loginWithCredentials, 
    registerWithEmail as registerWithEmailApi,
    verifyRegistrationEmail as verifyRegistrationEmailApi,
    retryRegistrationEmailVerification as retryRegistrationEmailVerificationApi,
    setCredentials, 
    changePassword, 
    restorePassword as restorePasswordApi, 
    verifyRestoreCode as verifyRestoreCodeApi, 
    setRestoredPassword as setRestoredPasswordApi, 
    type LoginRequestPayload, 
    type RegisterRequestPayload,
    type RegisterResponse,
    type VerifyRegistrationEmailPayload,
    type RetryRegistrationEmailVerificationPayload,
    type SetCredentialsPayload, 
    type ChangePasswordPayload, 
    type RestorePasswordPayload, 
    type VerifyRestoreCodePayload, 
    type SetRestoredPasswordPayload, 
    type RestorePasswordResponse, 
    type VerifyRestoreCodeResponse, 
    type SetRestoredPasswordResponse 
} from '@/shared/repositories/AuthRepository'

/**
 * Composable для работы с авторизацией
 */
export function useAuth() {
    const userStore = useUserStore()
    const sessionStore = useSessionStore()

    // Computed свойства
    const user = computed(() => userStore.user)
    const token = computed(() => sessionStore.token)
    const isAuthenticated = computed(() => userStore.isAuthenticated() && sessionStore.isAuthenticated())

    // Методы
    const login = async () => {
        try {
            const user = await authorize()
            return user
        } catch (error) {
            console.error('Ошибка авторизации:', error)
            throw error
        }
    }

    const logoutUser = () => {
        logout()
    }

    const loginByCredentials = async (credentials: LoginRequestPayload) => {
        const authData = await loginWithCredentials(credentials)
        sessionStore.setToken(authData.token)
        userStore.setUser(authData.user)
        return authData.user
    }

    const registerWithEmail = async (payload: RegisterRequestPayload): Promise<RegisterResponse> => {
        return registerWithEmailApi(payload)
    }

    const verifyRegistrationEmail = async (payload: VerifyRegistrationEmailPayload) => {
        const authData = await verifyRegistrationEmailApi(payload)
        sessionStore.setToken(authData.token)
        userStore.setUser(authData.user)
        return authData.user
    }

    const retryRegistrationEmailVerification = async (
        payload: RetryRegistrationEmailVerificationPayload
    ): Promise<RegisterResponse> => {
        return retryRegistrationEmailVerificationApi(payload)
    }

    const setUserCredentials = async (payload: SetCredentialsPayload) => {
        const response = await setCredentials(payload)
        if (response.success) {
            await userStore.fetchUserInfo()
        }
        return response
    }

    const changeUserPassword = async (payload: ChangePasswordPayload) => {
        return changePassword(payload)
    }

    const restorePassword = async (payload: RestorePasswordPayload): Promise<RestorePasswordResponse> => {
        return restorePasswordApi(payload)
    }

    const verifyRestoreCode = async (payload: VerifyRestoreCodePayload): Promise<VerifyRestoreCodeResponse> => {
        return verifyRestoreCodeApi(payload)
    }

    const setRestoredPassword = async (payload: SetRestoredPasswordPayload): Promise<SetRestoredPasswordResponse> => {
        return setRestoredPasswordApi(payload)
    }

    const checkAuth = () => {
        return checkAuthState()
    }

    // Методы для работы с токенами
    const validateToken = async () => {
        return await ensureValidToken()
    }

    const updateToken = async () => {
        return await refreshToken()
    }

    const forceUpdateToken = async () => {
        return await forceRefreshToken()
    }

    const checkTokenExpiration = () => {
        if (!token.value) return true
        return isTokenExpired(token.value)
    }

    const checkTokenExpiringSoon = (thresholdSeconds = 300) => {
        if (!token.value) return true
        return isTokenExpiringSoon(token.value, thresholdSeconds)
    }

    return {
        // Состояние
        user,
        token,
        isAuthenticated,

        // Методы авторизации
        login,
        loginWithCredentials: loginByCredentials,
        registerWithEmail,
        verifyRegistrationEmail,
        retryRegistrationEmailVerification,
        setCredentials: setUserCredentials,
        changePassword: changeUserPassword,
        restorePassword,
        verifyRestoreCode,
        setRestoredPassword,
        logout: logoutUser,
        checkAuth,

        // Методы работы с токенами
        validateToken,
        refreshToken: updateToken,
        forceRefreshToken: forceUpdateToken,
        isTokenExpired: checkTokenExpiration,
        isTokenExpiringSoon: checkTokenExpiringSoon,
    }
}
